import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BookOpen, Clock, CheckCircle, AlertCircle, Moon, Sun, LogOut, TrophyIcon } from 'lucide-react';
import { motion } from 'framer-motion';

const DashboardEstudiante = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAsignaciones = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/asignaciones/estudiante`,
          { withCredentials: true }
        );
        setAsignaciones(data);
      } catch (error) {
        console.error('Error fetching asignaciones:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAsignaciones();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getEstadoActividad = (asignacion) => {
    const ahora = new Date();
    const apertura = new Date(asignacion.fecha_apertura);
    const cierre = new Date(asignacion.fecha_cierre);

    if (ahora < apertura) return { estado: 'pendiente', color: 'bg-yellow-500', texto: 'Próximamente' };
    if (ahora > cierre) return { estado: 'vencida', color: 'bg-red-500', texto: 'Vencida' };
    if (asignacion.intentos_realizados > 0) return { estado: 'completada', color: 'bg-green-500', texto: 'Completada' };
    return { estado: 'activa', color: 'bg-blue-500', texto: 'Activa' };
  };

  const activas = asignaciones.filter(a => getEstadoActividad(a).estado === 'activa');
  const completadas = asignaciones.filter(a => getEstadoActividad(a).estado === 'completada');
  const pendientes = asignaciones.filter(a => getEstadoActividad(a).estado === 'pendiente');
  const vencidas = asignaciones.filter(a => getEstadoActividad(a).estado === 'vencida');

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Header */}
      <header 
        className="relative overflow-hidden border-b border-border"
        style={{
          background:
            'radial-gradient(circle at top left, rgba(0, 85, 255, 0.2), transparent 28%), radial-gradient(circle at bottom right, rgba(255, 200, 0, 0.18), transparent 30%), linear-gradient(135deg, rgba(248, 250, 252, 0.96), rgba(226, 232, 240, 0.75))'
        }}
      >
        <div className="backdrop-blur-sm bg-background/70">
          <div className="container mx-auto px-4 py-12">
            <div className="flex items-start justify-between">
              <div>
                <motion.h1 
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  ¡Hola, {user?.name?.split(' ')[0]}!
                </motion.h1>
                <motion.p 
                  className="text-base text-muted-foreground"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  Bienvenido a tu portal de aprendizaje
                </motion.p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleTheme}
                  data-testid="theme-toggle-button"
                >
                  {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  data-testid="logout-button"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Bento Grid Style */}
      <main className="container mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6 mb-8">
          <motion.div 
            className="md:col-span-4 lg:col-span-3"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-2 border-primary/20 bg-primary/5 h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <BookOpen className="h-8 w-8 text-primary" />
                  <Badge className="bg-primary text-primary-foreground">{activas.length}</Badge>
                </div>
                <CardTitle className="text-2xl">Activas</CardTitle>
                <CardDescription>Actividades disponibles ahora</CardDescription>
              </CardHeader>
            </Card>
          </motion.div>

          <motion.div 
            className="md:col-span-4 lg:col-span-3"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-2 border-green-500/20 bg-green-500/5 h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                  <Badge className="bg-green-600 text-white">{completadas.length}</Badge>
                </div>
                <CardTitle className="text-2xl">Completadas</CardTitle>
                <CardDescription>Actividades finalizadas</CardDescription>
              </CardHeader>
            </Card>
          </motion.div>

          <motion.div 
            className="md:col-span-4 lg:col-span-3"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="border-2 border-yellow-500/20 bg-yellow-500/5 h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Clock className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                  <Badge className="bg-yellow-600 text-white">{pendientes.length}</Badge>
                </div>
                <CardTitle className="text-2xl">Pendientes</CardTitle>
                <CardDescription>Próximas actividades</CardDescription>
              </CardHeader>
            </Card>
          </motion.div>

          <motion.div 
            className="md:col-span-4 lg:col-span-3"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="border-2 border-red-500/20 bg-red-500/5 h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                  <Badge className="bg-red-600 text-white">{vencidas.length}</Badge>
                </div>
                <CardTitle className="text-2xl">Vencidas</CardTitle>
                <CardDescription>Plazo expirado</CardDescription>
              </CardHeader>
            </Card>
          </motion.div>
        </div>

        {/* Activities List */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Mis Actividades</h2>
          </div>

          {loading ? (
            <div className="grid gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-6 bg-muted rounded w-1/3 mb-2"></div>
                    <div className="h-4 bg-muted rounded w-1/2"></div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : asignaciones.length === 0 ? (
            <Card className="border-2 border-dashed border-border">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <div className="w-24 h-24 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
                  <BookOpen className="w-10 h-10" />
                </div>
                <p className="text-lg text-muted-foreground text-center">No tienes actividades asignadas aún</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4" data-testid="actividades-list">
              {asignaciones.map((asignacion, index) => {
                const estadoInfo = getEstadoActividad(asignacion);
                return (
                  <motion.div
                    key={asignacion.asignacion_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card 
                      className="hover:shadow-lg transition-all cursor-pointer border-border"
                      onClick={() => navigate(`/estudiante/actividad/${asignacion.actividad_id}/${asignacion.asignacion_id}`)}
                      data-testid={`actividad-card-${index}`}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={estadoInfo.color}>{estadoInfo.texto}</Badge>
                              <Badge variant="outline">{asignacion.actividad?.tipo_actividad}</Badge>
                            </div>
                            <CardTitle className="text-xl mb-2">{asignacion.actividad?.titulo}</CardTitle>
                            <CardDescription>{asignacion.actividad?.descripcion}</CardDescription>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            <span>Cierra: {new Date(asignacion.fecha_cierre).toLocaleDateString()}</span>
                          </div>
                          {asignacion.intentos_realizados > 0 && (
                            <div className="flex items-center gap-1">
                              <TrophyIcon className="h-4 w-4" />
                              <span>Intentos: {asignacion.intentos_realizados}/{asignacion.actividad?.max_intentos}</span>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardEstudiante;
